import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Modal, ModalBody, ModalHeader, Table } from 'reactstrap';
import Swal from 'sweetalert2';
import { AssignProjectMilestone } from '../../../../domain/projectMilestones/assignProjectMilestone';
import { ProjectMilestoneListModel } from '../../../../domain/projectMilestones/projectMilestoneListModel';
import { ProjectMilestoneRepository } from '../../../../infrastructure/repositories/projectMilestones/projectMilestoneRepository';
import ModalAssignMilestonesSection from '../form/ModalAssignMilestonesSection';
import ModalCreateMilestoneTaskSection from '../../../project-milestones/components/form/ModalCreateMilestoneTaskSection';
import { CreateMilestoneTask } from '../../../../domain/projectMilestones/createMilestoneTask';
import { ProjectMilestoneTask } from '../../../../domain/projectMilestones/projectMilestonetask';
import { UpdateMilestoneTaskModel } from '../../../../domain/projectMilestones/updateMilestoneTaskModel';

interface Props {
    updateProjectPercentage: () => void;
}

export default function ProjectMilestoneListTable({
    updateProjectPercentage,
}: Props) {
    const [modal, setModal] = useState(false);
    const [editingPercentageIndex, setEditingPercentageIndex] = useState<number>(-1);
    const [refresh, setRefresh] = useState<number>(1);
    const [errorMessage, setErrorMessage] = useState('');
    const [milestones, setProjecMilestones] = useState<ProjectMilestoneListModel[]>(
        []
    );

    const [isSubmittingModal, setIsSubmittingModal] = useState<boolean>(false);
    const [selectedMilestonesModal, setSelectedMilestonesModal] = useState<
        AssignProjectMilestone[]
    >([]);

    const openModal = () => setModal(!modal);

    const { id } = useParams<{ id: string }>();

    const handleError = (error: any) => {
        setErrorMessage(error.response.data);
        window.scrollTo({ behavior: 'smooth', left: 0, top: 0 });
    };

    const handleEditClick = (index: number) => {
        setEditingPercentageIndex(index);
    };

    const handleSaveClick = async (index: number) => {
        if (!milestones || index < 0 || index >= milestones.length) {
            console.error('Índice de hito no válido o hitos indefinido/vacío');
            return;
        }
        const milestoneToUpdate = milestones[index];

        try {
            await ProjectMilestoneRepository.UpdateProjectMilestoneProject(
                {
                    projectId: Number(id),
                    plannedPercentage: milestoneToUpdate.plannedPercentage,
                    percentageRealized: milestoneToUpdate.percentageRealized,
                },
                milestoneToUpdate.id
            );
            updateProjectPercentage();
            setEditingPercentageIndex(-1);
        } catch (error) {
            console.error('Error al actualizar:', error);
        }
    };

    let onRefresh = () => {
        setRefresh(refresh + 1);
    };

    useEffect(() => {
        if (id) {
            ProjectMilestoneRepository.GetProjectMilestoneListTable(id).then(
                (response) => {
                    setProjecMilestones(response);
                }
            );
        }
    }, [id, refresh]);

    const onChangeProjectMileStone = (
        index: number,
        value: string | number,
        name: string
    ) => {
        const numericValue = parseFloat(value.toString());

        if (numericValue <= 100) {
            setProjecMilestones(
                milestones.map((st, i) => {
                    if (i === index) {
                        return {
                            ...st,
                            [name]: value,
                        };
                    }
                    return st;
                })
            );
        } else {
            setErrorMessage(`El valor no puede ser mayor que 100%`);
            setTimeout(() => {
                setErrorMessage('');
            }, 3000);
        }
    };

    const handleSubmitModal = (event: React.FormEvent) => {
        event.preventDefault();

        setIsSubmittingModal(true);
        setErrorMessage('');

        if (id && selectedMilestonesModal.length > 0) {
            ProjectMilestoneRepository.AssignProjectMilestone(
                id,
                selectedMilestonesModal
            )
                .then((response) => {
                    setModal(false);
                    setIsSubmittingModal(false);
                    onRefresh();
                    Swal.fire('Hito asignado exitosamente!', '', 'success');
                    setSelectedMilestonesModal([]);
                })
                .catch(handleError);
        }
    };
    
    const [expandedTasks, setExpandedTasks] = useState<Record<number, boolean>>({});

    const [isAddTaskModalOpen, setIsAddTaskModalOpen] = useState(false)
    const [selectedMilestoneId, setSelectedMilestoneId] = useState<number | null>(null);

    const [createMilestoneTaskData, setCreateMilestoneTaskData] = useState<CreateMilestoneTask>({
        task: '',
        comments: '',
        estimatedHours: 0,
        actualWorkHours: 0,
        completedDate: null,
    });        
    const isFormValid =
        createMilestoneTaskData.comments.trim() !== '' &&
        createMilestoneTaskData.estimatedHours > 0;

    const openAddTaskModal = (milestoneId: number) => {
        setSelectedMilestoneId(milestoneId);
        setIsAddTaskModalOpen(true);
    };

    const resetAddTaskForm = () => {
        setCreateMilestoneTaskData({
            task: '',
            comments: '',
            estimatedHours: 0,
            actualWorkHours: 0,
            completedDate: null,
        });
    };
    
    const handleSubmitModalAddTask = (milestoneId: number | null) => {
        if (milestoneId === null) return;

        setIsSubmittingModal(true);
        setErrorMessage('');
        
        ProjectMilestoneRepository.CreateTasksByMilestone(milestoneId, createMilestoneTaskData)
            .then((response) => {
                reloadMilestones()
                setIsAddTaskModalOpen(false);
                resetAddTaskForm()
                Swal.fire('Tarea creada exitosamente!', '', 'success');
            })
            .catch((error) => {
                setErrorMessage('No se pudo crear la tarea');
                Swal.fire('No se pudo crear la tarea', '', 'error');
            })                
            .finally(() => {
                setIsSubmittingModal(false);
            });
    };

    /**
     * Reloads the milestone list for the current project.
     * If a valid project ID exists, it fetches the latest milestones
     * from the API and updates the state accordingly.
     */
    const reloadMilestones = () =>{
        if (id) {
            ProjectMilestoneRepository.GetProjectMilestoneListTable(id).then(
                (response) => {
                    setProjecMilestones(response);
                }
            );
        }
    }

    const toggleTasks = (id: number) => {
        setExpandedTasks((prevExpanded) => ({
            ...prevExpanded,
            [id]: !prevExpanded[id],
        }));
    };
    
    /**
     * Edit task
     */

    const [editingTaskIndex, setEditingTaskIndex] = useState<{
        milestoneIndex: number;
        taskIndex: number;
    } | null>(null);
    
    const [originalTaskData, setOriginalTaskData] = useState<ProjectMilestoneTask | null>(null);
    const handleEditTaskClick = (milestoneIndex: number, taskIndex: number) => {
        const taskToEdit = milestones[milestoneIndex].tasks[taskIndex];

        setOriginalTaskData({ ...taskToEdit });
        setEditingTaskIndex({ milestoneIndex, taskIndex });
    };

    const handleTaskCancelClick = (milestoneIndex: number, taskIndex: number) => {
        if (originalTaskData) {
            const updatedMilestones = [...milestones];

            updatedMilestones[milestoneIndex].tasks[taskIndex] = originalTaskData;
            setProjecMilestones(updatedMilestones);
        }

        setEditingTaskIndex(null);
        setOriginalTaskData(null);
    };
    
    const onChangeTaskMileStone = (
        milestoneIndex: number,
        taskIndex: number,
        value: string | number | null,
        name: keyof ProjectMilestoneTask
    ) => {
        const updatedMilestones = [...milestones];
        const updatedTask = { ...updatedMilestones[milestoneIndex].tasks[taskIndex], [name]: value };

        updatedMilestones[milestoneIndex].tasks[taskIndex] = updatedTask;
        setProjecMilestones(updatedMilestones);
    };
    
    const handleTaskSaveClick = async (id: number) => {
        if (!editingTaskIndex) return;
    
        const { milestoneIndex, taskIndex } = editingTaskIndex;
        const updatedTask = milestones[milestoneIndex].tasks[taskIndex];    
        const model: UpdateMilestoneTaskModel = {
            comments: updatedTask.comments,
            estimatedHours: updatedTask.estimatedHours,
            actualWorkHours: updatedTask.actualWorkHours,
            completedDate: updatedTask.completedDate
                ? new Date(updatedTask.completedDate)
                : null,
        };
    
        try {
            await ProjectMilestoneRepository.UpdateProjectMilestoneTask(id, model);
            Swal.fire('Tarea actualizada exitosamente!', '', 'success');
    
            setEditingTaskIndex(null);
            setOriginalTaskData(null);
        } catch (error: any) {
            Swal.fire('Error al guardar la tarea', error.message || 'Error desconocido', 'error');
        }
    };
    
    return (
        <React.Fragment>
            <div className='m-3'>
                <button
                    type='button'
                    className='btn btn-primary'
                    onClick={openModal}
                >
                    <i className='ri-add-box-fill me-1 align-bottom'></i>
                    Agregar
                </button>
            </div>
            {errorMessage && (
                <div className='text-danger mb-2 bg-soft-danger p-1 px-2 w-50 rounded'>
                    {errorMessage}
                </div>
            )}
            <Table className='table table-hover '>
                <thead className='table-light'>
                    <tr>
                        <th scope='col'>Hito</th>
                        <th style={{ textAlign: 'center' }} scope='col'>
                            Peso
                        </th>
                        <th style={{ textAlign: 'center' }} scope='col'>
                            Porcentaje Planeado
                        </th>
                        <th style={{ textAlign: 'center' }} scope='col'>
                            Porcentaje Realizado
                        </th>
                        <th></th>
                    </tr>
                </thead>

                <tbody className='table-group-divider'>
                    {milestones?.map((milestone, index) => (
                        <React.Fragment key={milestone.id}>
                        <tr key={milestone.id}>
                            <td>{milestone.name}</td>
                            <td style={{ textAlign: 'center' }}>
                                {milestone.weight}%
                            </td>
                            <td style={{ textAlign: 'center' }}>
                                {editingPercentageIndex === index ? (
                                    <input
                                        type='number'
                                        value={milestone.plannedPercentage}
                                        onChange={(e) => {
                                            onChangeProjectMileStone(
                                                index,
                                                e.target.value,
                                                'plannedPercentage'
                                            );
                                        }}
                                    />
                                ) : (
                                    `${milestone.plannedPercentage}%`
                                )}
                            </td>
                            <td style={{ textAlign: 'center' }}>
                                {editingPercentageIndex === index ? (
                                    <input
                                        type='number'
                                        value={milestone.percentageRealized}
                                        onChange={(e) => {
                                            onChangeProjectMileStone(
                                                index,
                                                e.target.value,
                                                'percentageRealized'
                                            );
                                        }}
                                    />
                                ) : (
                                    `${milestone.percentageRealized}%`
                                )}
                            </td>
                            <td>
                                <div
                                    style={{
                                        display: 'flex',
                                        float: 'right',
                                        gap: '0.5rem',
                                    }}
                                >
                                    {editingPercentageIndex === index ? (
                                        <button
                                            type='button'
                                            title='Guardar'
                                            onClick={() => handleSaveClick(index)}
                                            className='btn btn-sm btn-primary ms-auto'
                                        >
                                            <i className='mdi mdi-content-save'></i>
                                        </button>
                                    ) : (
                                        <>
                                            <button
                                                title='Editar'
                                                type='button'
                                                className='btn btn-sm btn-info'
                                                onClick={() => handleEditClick(index)}
                                            >
                                                <i className='mdi mdi-pencil'></i>
                                            </button>
                                            <button
                                                type='button'
                                                title='Agregar tarea'
                                                className='btn btn-sm btn-success me-2'
                                                onClick={() => openAddTaskModal(milestone.id)}
                                            >
                                                <i className='mdi mdi-plus'></i>
                                            </button>
                                        </>    
                                    )}
                                    <button
                                        title='Ver tareas'
                                        type='button'
                                        className='btn btn-sm btn-warning'
                                        onClick={() =>
                                            toggleTasks(
                                                milestone.id
                                            )
                                        }
                                    >
                                       {expandedTasks[
                                            milestone.id
                                        ] ? (
                                               <i className='ri-arrow-down-s-line'></i>
                                            
                                            ) : (
                                               <i className='ri-arrow-up-s-line'></i>
                                        )}
                                    </button>
                                </div>
                            </td>
                        </tr>
                        {expandedTasks[
                            milestone.id
                        ] && (
                            <tr>
                                <td
                                colSpan={5}
                                className='px-5'>
                                <table className='table table-hover'>
                                    <thead>
                                        <tr>
                                            <th>Descripción</th>
                                            <th>Estimated Hours</th>
                                            <th>Actual Work Hours</th>
                                            <th>Completed Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {milestone.tasks.map(
                                            (task, indexTask)=>(
                                                <tr
                                                    key={
                                                        task.id                                                        
                                                    }>
                                                    <td>
                                                    {
                                                        editingTaskIndex &&
                                                        editingTaskIndex.milestoneIndex === index &&
                                                        editingTaskIndex.taskIndex === indexTask ? (
                                                        <input
                                                            type='text'
                                                            value={task.comments}
                                                            onChange={(e) => 
                                                                onChangeTaskMileStone(index, indexTask, e.target.value, 'comments')}

                                                        />
                                                    ) : (
                                                        `${task.comments}`
                                                    )}
                                                    </td>
                                                    <td>
                                                        {
                                                            editingTaskIndex &&
                                                            editingTaskIndex.milestoneIndex === index &&
                                                            editingTaskIndex.taskIndex === indexTask ? (
                                                            <input
                                                                type='number'
                                                                value={task.estimatedHours}
                                                                onChange={(e) =>
                                                                    onChangeTaskMileStone(index, indexTask, Number(e.target.value), 'estimatedHours')
                                                                }
                                                            />
                                                        ) : (
                                                            `${task.estimatedHours}`
                                                        )}
                                                    </td>
                                                    <td>
                                                        {
                                                            editingTaskIndex &&
                                                            editingTaskIndex.milestoneIndex === index &&
                                                            editingTaskIndex.taskIndex === indexTask ? (
                                                            <input
                                                                type='number'
                                                                value={task.actualWorkHours}
                                                                onChange={(e) =>
                                                                    onChangeTaskMileStone(index, indexTask, Number(e.target.value), 'actualWorkHours')
                                                                }
                                                            />
                                                        ) : (
                                                            `${task.actualWorkHours}`
                                                        )}
                                                    </td>
                                                    <td>
                                                    {
                                                        editingTaskIndex &&
                                                        editingTaskIndex.milestoneIndex === index &&
                                                        editingTaskIndex.taskIndex === indexTask ? (
                                                            <input
                                                                type='date'
                                                                value={task.completedDate ?? ''}
                                                                min={new Date().toISOString().split('T')[0]}
                                                                onChange={(e) => {
                                                                    const newDate = e.target.value === '' ? null : e.target.value;

                                                                    onChangeTaskMileStone(index, indexTask, newDate, 'completedDate');
                                                                }}
                                                            />
                                                        ) : (
                                                            task.completedDate ? (
                                                                new Date(task.completedDate).toLocaleDateString()
                                                            ) : (
                                                                ''
                                                            )
                                                        )}
                                                    </td>
                                                    <td>
                                                    {
                                                        editingTaskIndex &&
                                                        editingTaskIndex.milestoneIndex === index &&
                                                        editingTaskIndex.taskIndex === indexTask ? (
                                                            <>
                                                                <button
                                                                    type='button'
                                                                    title='Cancelar'
                                                                    onClick={() => handleTaskCancelClick(index, indexTask)}
                                                                    className='btn btn-sm btn-danger me-2'
                                                                >
                                                                    <i className='mdi mdi-close'></i>
                                                                </button>
                                                                <button
                                                                    type='button'
                                                                    title='Guardar'
                                                                    onClick={() => handleTaskSaveClick(task.id)}
                                                                    className='btn btn-sm btn-primary ms-auto'
                                                                >
                                                                    <i className='mdi mdi-content-save'></i>
                                                                </button>
                                                            </>
                                                        ) : (
                                                            <button
                                                                title='Editar'
                                                                type='button'
                                                                className='btn btn-sm btn-info'
                                                                onClick={() => handleEditTaskClick(index, indexTask)}
                                                            >
                                                                <i className='mdi mdi-pencil'></i>
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            )
                                        )}
                                    </tbody>
                                    </table>
                                </td>
                            </tr>
                        )}
                        </React.Fragment>
                    ))}
                </tbody>
            </Table>

            <Modal
                isOpen={modal}
                toggle={openModal}
                centered
                className='border-2'
                size='lg'
            >
                <ModalHeader toggle={openModal} className='p-3 ps-4 bg-soft-primary'>
                    Asignar Nuevo Hito
                </ModalHeader>
                <ModalBody className=''>
                    <div className='table-responsive '>
                        <ModalAssignMilestonesSection
                            assignMilestoneModal={selectedMilestonesModal}
                            setSelectedMilestonesModal={setSelectedMilestonesModal}
                        />
                    </div>
                </ModalBody>
                <div className='modal-footer'>
                    <button
                        type='button'
                        className='btn btn-light w-xs'
                        data-bs-dismiss='modal'
                        onClick={() => {
                            setModal(false);
                            setSelectedMilestonesModal([]);
                        }}
                    >
                        Cancelar
                    </button>
                    <button
                        type='button'
                        className='btn btn-primary w-xs'
                        onClick={handleSubmitModal}
                        disabled={
                            isSubmittingModal || selectedMilestonesModal.length === 0
                        }
                    >
                        Guardar
                    </button>
                </div>
            </Modal>
        
            <Modal
                isOpen={isAddTaskModalOpen}
                centered
                className='border-2'
                size='lg'
                backdrop='static'
                keyboard={false}
            >
                <ModalHeader className='p-3 ps-4 bg-soft-primary'>
                    Crear una tarea para el Hito
                </ModalHeader>
                <ModalBody style={{ overflow: 'hidden', maxWidth: '100%' }}>
                    <div className='container-fluid'>
                        <div className='row'>
                            <ModalCreateMilestoneTaskSection
                                createMilestoneTaskData={createMilestoneTaskData}
                                setCreateMilestoneTaskData={setCreateMilestoneTaskData}
                            />
                        </div>
                    </div>
                </ModalBody>
                    <div className='modal-footer'>
                        <button
                            type='button'
                            className='btn btn-light w-xs'
                            onClick={() => {
                                resetAddTaskForm();
                                setIsAddTaskModalOpen(false);
                            }}
                        >
                            Cancelar
                        </button>
                        <button
                            type='button'
                            className='btn btn-primary w-xs'
                            onClick={() => handleSubmitModalAddTask(selectedMilestoneId)}
                            disabled={!isFormValid || isSubmittingModal}
                        >
                            Guardar
                        </button>
                    </div>
            </Modal>
        </React.Fragment>
    );
}
